import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaArrowRight, FaShieldAlt } from 'react-icons/fa';
import theme from '../../theme';

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 120px 5% 70px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, ${theme.colors.primary} 100%);
  text-align: center;
  color: white;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 750px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.25rem;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2.2rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.75rem; }
`;

const HeroSubtitle = styled.p`
  font-size: 1.15rem;
  opacity: 0.85;
  line-height: 1.7;
  max-width: 650px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 0.95rem; }
`;

const ContentSection = styled.section`
  padding: 60px 5% 80px;
  background: ${theme.colors.lightGray};
`;

const ContentContainer = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 3rem;
`;

const CategoryTab = styled.button`
  padding: 0.7rem 1.4rem;
  border: none;
  border-radius: 50px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${props => props.$active ? '#0f172a' : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.secondary};
  box-shadow: ${props => props.$active
    ? '0 4px 15px rgba(15, 23, 42, 0.25)'
    : '0 2px 8px rgba(0,0,0,0.06)'};

  &:hover {
    background: ${props => props.$active ? '#0f172a' : '#f1f5f9'};
    transform: translateY(-1px);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.55rem 1rem;
    font-size: 0.8rem;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid ${props => props.$open ? 'rgba(139, 0, 0, 0.15)' : '#e5e7eb'};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  ${props => props.$open && `box-shadow: 0 4px 16px rgba(0,0,0,0.07);`}
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.2rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${props => props.$open ? '#8B0000' : theme.colors.secondary};
  transition: color 0.3s ease;

  &:hover { color: #8B0000; }

  svg {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    color: ${theme.colors.darkGray};
    transition: transform 0.3s ease;
    transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const FAQAnswer = styled.div`
  max-height: ${props => props.$open ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.35s ease, padding 0.35s ease;
  padding: ${props => props.$open ? '0 1.5rem 1.25rem' : '0 1.5rem'};

  p {
    font-size: 0.9rem;
    color: ${theme.colors.darkGray};
    line-height: 1.75;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: ${theme.colors.darkGray};
  font-size: 0.9rem;
`;

const CTASection = styled.section`
  padding: 70px 5%;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -40%;
    right: -15%;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(139,0,0,0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 550px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 800;
  margin-bottom: 1.75rem;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.35rem; }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2.25rem;
  background: #8B0000;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(139, 0, 0, 0.3);

  &:hover {
    background: #6b0000;
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(139, 0, 0, 0.45);
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }
  &:hover svg { transform: translateX(4px); }
`;

const CTATrust = styled.p`
  margin-top: 1rem;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  svg { width: 12px; height: 12px; }
`;

const FAQPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [activeCategory, setActiveCategory] = useState('general');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const d = (key) => t(`faqPage.${key}`);

  const categories = [
    { id: 'general', labelKey: 'categories.general' },
    { id: 'pricing', labelKey: 'categories.pricing' },
    { id: 'technical', labelKey: 'categories.technical' },
    { id: 'operations', labelKey: 'categories.operations' },
    { id: 'support', labelKey: 'categories.support' },
  ];

  const faqItems = [
    { id: 'g1', category: 'general', qKey: 'items.g1.q', aKey: 'items.g1.a' },
    { id: 'g2', category: 'general', qKey: 'items.g2.q', aKey: 'items.g2.a' },
    { id: 'g3', category: 'general', qKey: 'items.g3.q', aKey: 'items.g3.a' },
    { id: 'p1', category: 'pricing', qKey: 'items.p1.q', aKey: 'items.p1.a' },
    { id: 'p2', category: 'pricing', qKey: 'items.p2.q', aKey: 'items.p2.a' },
    { id: 'p3', category: 'pricing', qKey: 'items.p3.q', aKey: 'items.p3.a' },
    { id: 'p4', category: 'pricing', qKey: 'items.p4.q', aKey: 'items.p4.a' },
    { id: 't1', category: 'technical', qKey: 'items.t1.q', aKey: 'items.t1.a' },
    { id: 't2', category: 'technical', qKey: 'items.t2.q', aKey: 'items.t2.a' },
    { id: 't3', category: 'technical', qKey: 'items.t3.q', aKey: 'items.t3.a' },
    { id: 't4', category: 'technical', qKey: 'items.t4.q', aKey: 'items.t4.a' },
    { id: 'o1', category: 'operations', qKey: 'items.o1.q', aKey: 'items.o1.a' },
    { id: 'o2', category: 'operations', qKey: 'items.o2.q', aKey: 'items.o2.a' },
    { id: 'o3', category: 'operations', qKey: 'items.o3.q', aKey: 'items.o3.a' },
    { id: 's1', category: 'support', qKey: 'items.s1.q', aKey: 'items.s1.a' },
    { id: 's2', category: 'support', qKey: 'items.s2.q', aKey: 'items.s2.a' },
  ];

  const filtered = faqItems.filter(item => item.category === activeCategory);

  return (
    <>
      <SEO titleKey="faqPage.seo.title" descriptionKey="faqPage.seo.description" />

      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>{d('hero.title')}</HeroTitle>
            <HeroSubtitle>{d('hero.subtitle')}</HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <ContentSection>
          <ContentContainer>
            <CategoryTabs>
              {categories.map(cat => (
                <CategoryTab
                  key={cat.id}
                  $active={activeCategory === cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {d(cat.labelKey)}
                </CategoryTab>
              ))}
            </CategoryTabs>

            <FAQList>
              {filtered.length > 0 ? (
                filtered.map(item => (
                  <FAQItem key={item.id} $open={openItems[item.id]}>
                    <FAQQuestion $open={openItems[item.id]} onClick={() => toggleItem(item.id)}>
                      {d(item.qKey)}
                      <FaChevronDown />
                    </FAQQuestion>
                    <FAQAnswer $open={openItems[item.id]}>
                      <p>{d(item.aKey)}</p>
                    </FAQAnswer>
                  </FAQItem>
                ))
              ) : (
                <EmptyState>{d('emptyState')}</EmptyState>
              )}
            </FAQList>
          </ContentContainer>
        </ContentSection>

        <CTASection>
          <CTAContent>
            <CTATitle>{d('cta.title')}</CTATitle>
            <CTAButton to={`/${lang}/demo`}>
              {d('cta.button')} <FaArrowRight />
            </CTAButton>
            <CTATrust>
              <FaShieldAlt /> {d('cta.trustNote')}
            </CTATrust>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

export default FAQPage;
